/**
 * Base Service Class
 * Fornisce funzionalità comuni per tutti i service API
 */

import logger from '../Logger';
// NOTA: AuthService NON viene importato qui per evitare dipendenze circolari
// Usa import dinamico dove necessario: const AuthService = (await import('./AuthService')).default;
import ConfigService from '../ConfigService';

export interface ApiConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface ApiError {
  message: string;
  statusCode?: number;
  details?: any;
  timestamp: Date;
}

export class BaseService {
  protected baseURL: string;
  protected timeout: number;
  protected defaultHeaders: Record<string, string>;

  constructor(config?: ApiConfig) {
    // IMPORTANTE: Usa ConfigService per ottenere la base URL dal config.json runtime
    // Questo permette di cambiare l'URL senza ricompilare il frontend

    // Priorità:
    // 1. config.baseURL passato esplicitamente
    // 2. ConfigService (caricato dal config.json runtime)
    // 3. REACT_APP_API_BASE_URL (build-time environment variable)
    // 4. fallback a URL relativo basato su window.location.origin

    let baseURL: string;

    if (config?.baseURL) {
      baseURL = config.baseURL;
    } else {
      const configServiceURL = ConfigService.getApiBaseUrl();
      // ConfigService ritorna "/" se la config non è ancora caricata
      // In development, se il config non è caricato e siamo su localhost:3000,
      // usa localhost:3002 (proxy debug) come fallback
      if (configServiceURL === '/' && window.location.hostname === 'localhost' && window.location.port === '3000') {
        baseURL = 'http://localhost:3002/';
      } else {
        baseURL = configServiceURL || window.location.origin + '/';
      }
    }

    this.baseURL = baseURL;

    console.log('🔧 BaseService initialized with baseURL:', this.baseURL);

    this.timeout = config?.timeout || 30000; // 30 seconds default
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config?.headers
    };
  }

  /**
   * Gestisce le richieste HTTP con error handling centralizzato
   */
  protected async request<T>(
    endpoint: string,
    options: RequestInit & { isRetry?: boolean; timeout?: number } = {}
  ): Promise<T> {
    const controller = new AbortController();
    const requestTimeout = options.timeout || this.timeout;
    const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

    const url = `${this.baseURL}${endpoint}`;

    // Log headers being sent
    const finalHeaders = {
      ...this.defaultHeaders,
      ...options.headers
    };
    
    // Logging migliorato per debug
    console.log(`🔑 [${new Date().toISOString()}] API Call:`, {
      endpoint,
      method: options.method || 'GET',
      hasToken: !!(finalHeaders as any)['Authorization'],
      tokenLength: (finalHeaders as any)['Authorization']?.length,
      isRetry: options.isRetry || false
    });
    
    logger.debug(`API Request: ${options.method || 'GET'} ${url}`, {
      headers: finalHeaders,
      hasAuthHeader: !!(finalHeaders as any)['Authorization']
    });
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: finalHeaders,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Log delle richieste in development
      logger.debug(`API Response: ${options.method || 'GET'} ${url}`, {
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        // Gestione speciale per errori 401
        // Il proxy gestisce automaticamente i token, quindi un 401 significa autenticazione fallita
        if (response.status === 401 && !options.isRetry) {
          console.log('🔄 Errore 401 ricevuto - autenticazione fallita');

          try {
            // Importa AuthService dinamicamente per evitare dipendenze circolari
            const AuthService = (await import('./AuthService')).default;

            // Non proviamo più a ottenere un nuovo token client (gestito dal proxy)
            // Un 401 ora significa che l'utente deve rifare login
            const newToken = AuthService.getToken();

            if (newToken && !options.isRetry) {
              console.log('✅ Riprovo la richiesta con token esistente...');

              // Riprova la richiesta UNA volta con il token esistente
              const retryOptions = {
                ...options,
                headers: {
                  ...options.headers,
                  'Authorization': `Bearer ${newToken}`
                },
                isRetry: true
              };
              
              return this.request<T>(endpoint, retryOptions);
            }
          } catch (retryError) {
            console.error('❌ Impossibile ottenere nuovo token:', retryError);
            
            // Se non riusciamo a ottenere un nuovo token, resettiamo tutto
            console.log('🔴 Reset completo dello stato utente');
            
            // Pulisci tutto lo storage
            sessionStorage.clear();
            localStorage.removeItem('clientToken');
            localStorage.removeItem('tokenExpiry');
            localStorage.removeItem('persist:root');
            
            // Forza il reload per tornare al login
            if (typeof window !== 'undefined') {
              window.location.href = '/';
            }
          }
        }
        
        throw await this.handleErrorResponse(response);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw this.createError('Request timeout', 408);
        }
      }
      
      throw this.handleError(error);
    }
  }

  /**
   * GET request
   */
  protected async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET'
    });
  }

  /**
   * POST request
   */
  protected async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request
   */
  protected async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request
   */
  protected async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE'
    });
  }

  /**
   * POST request con FormData
   */
  protected async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    // Import dinamico per evitare dipendenze circolari
    const AuthService = (await import('./AuthService')).default;
    const token = AuthService.getToken();

    logger.info('API Call (FormData):', {
      endpoint,
      method: 'POST',
      hasToken: !!token
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          // Non impostare Content-Type per FormData, il browser lo fa automaticamente
        },
        body: formData
      });

      if (!response.ok) {
        const error = await this.handleErrorResponse(response);
        throw error;
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      logger.error('FormData POST request failed:', error);
      throw error;
    }
  }

  /**
   * Gestisce errori dalle response HTTP
   */
  private async handleErrorResponse(response: Response): Promise<ApiError> {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let details = null;

    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        // Il backend usa "Messaggio" (con M maiuscola), gestiamo anche "message" e "error" per compatibilità
        message = errorData.Messaggio || errorData.messaggio || errorData.message || errorData.error || message;
        details = errorData;
      }
    } catch {
      // Se non riusciamo a parsare l'errore, usiamo il messaggio default
    }

    return this.createError(message, response.status, details);
  }

  /**
   * Gestione centralizzata degli errori
   */
  private handleError(error: unknown): ApiError {
    // Se è già un ApiError (es. da handleErrorResponse), ritornalo così com'è
    if (error && typeof error === 'object' && 'message' in error && 'statusCode' in error) {
      return error as ApiError;
    }

    if (error instanceof Error) {
      logger.error('API Error:', error);
      return this.createError(error.message);
    }

    logger.error('Unknown API Error:', error);
    return this.createError('An unexpected error occurred');
  }

  /**
   * Crea un oggetto errore standardizzato
   */
  protected createError(message: string, statusCode?: number, details?: any): ApiError {
    return {
      message,
      statusCode,
      details,
      timestamp: new Date()
    };
  }

  /**
   * Aggiunge il token di autenticazione alle headers
   */
  protected withAuth(token: string): Record<string, string> {
    return {
      ...this.defaultHeaders,
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Retry logic per richieste fallite
   */
  protected async withRetry<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: any;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;
        
        // Non fare retry per errori client (4xx)
        if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
          throw error;
        }
        
        // Attendi prima del prossimo retry
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
        }
      }
    }
    
    throw lastError;
  }
}